import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Add, Delete, Edit } from "@mui/icons-material";
import { Alert, Box, Button, Tab, Tabs, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { ColDef, GetRowIdParams, GridOptions, ICellRendererParams } from "ag-grid-community";
import { useSnackbar } from "notistack";

import { useDeleteImageOverlayMutation, useDeleteOverlayMutation, useGetImageOverlaysQuery, useGetOverlaysQuery } from "fond/api/overlaysSlice";
import { MAX_OVERLAY_COUNT } from "fond/constants";
import { Store } from "fond/types";
import { CustomOverlayItem, ImageOverlayItem } from "fond/types/overlays";
import { formatNumber } from "fond/utils/number";
import { isMultiProject } from "fond/utils/project";
import { AgGrid, ConfirmModal } from "fond/widgets";
import { IconButtonCellRenderer, LinkCellRenderer } from "fond/widgets/AgGrid";
import Modal from "fond/widgets/Modal";

import EditOverlayModal from "./EditOverlayModal";

interface OverlaysSettingModalProps {
  open: boolean;
  onClose: () => void;
  onOpenImageOverlay: () => void;
}

const OverlaysSettingModal: React.FC<OverlaysSettingModalProps> = ({ open, onClose, onOpenImageOverlay }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [editingOverlay, setEditingOverlay] = useState<CustomOverlayItem | null>(null);
  const [removingProjectOverlay, setRemovingProjectOverlay] = useState<CustomOverlayItem | null>(null);
  const [removingImageOverlay, setRemovingImageOverlay] = useState<ImageOverlayItem | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [deleteOverlay] = useDeleteOverlayMutation();
  const [deleteImageOverlay] = useDeleteImageOverlayMutation();
  const versionId = useSelector((state: Store) => state.project.versionId);
  const project = useSelector((state: Store) => state.project.projects[state.project.projectId]?.project);
  const multiProjectId = project && isMultiProject(project) ? project.ID : undefined;

  // Determine query parameter for overlays
  let overlaysQueryParam;
  if (multiProjectId) {
    overlaysQueryParam = { multiProjectId };
  } else if (versionId) {
    overlaysQueryParam = { versionId };
  } else {
    overlaysQueryParam = skipToken;
  }

  // Get project overlays from the API
  const { data: overlaysData } = useGetOverlaysQuery(overlaysQueryParam, {
    skip: !versionId && !multiProjectId,
  });

  // Get image overlays
  const { data: imageOverlayData } = useGetImageOverlaysQuery(overlaysQueryParam, {
    skip: !versionId && !multiProjectId,
  });

  const overlayItems = overlaysData?.CustomOverlays ?? [];
  const imageOverlayItems = imageOverlayData?.ImageOverlays ?? [];
  const maxOverlays = overlayItems.length >= MAX_OVERLAY_COUNT;

  const handleRemoveProjectOverlay = useCallback(async () => {
    if (!removingProjectOverlay) return;
    try {
      await deleteOverlay({
        overlayId: removingProjectOverlay.ID,
        ...(multiProjectId ? { multiProjectId } : { versionId }),
      }).unwrap();
      enqueueSnackbar(`Overlay "${removingProjectOverlay.Name}" deleted successfully.`, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(`Failed to delete overlay "${removingProjectOverlay.Name}".`, { variant: "error" });
    } finally {
      setRemovingProjectOverlay(null);
    }
  }, [removingProjectOverlay, deleteOverlay, enqueueSnackbar, versionId, multiProjectId]);

  const handleRemoveImageOverlay = useCallback(async () => {
    if (!removingImageOverlay) return;
    const targetId = multiProjectId || versionId;
    try {
      await deleteImageOverlay({
        imageOverlayId: removingImageOverlay.ID,
        targetId,
      }).unwrap();
      enqueueSnackbar(`Image "${removingImageOverlay.Name}" deleted successfully.`, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(`Failed to delete image "${removingImageOverlay.Name}".`, { variant: "error" });
    } finally {
      setRemovingImageOverlay(null);
    }
  }, [removingImageOverlay, deleteImageOverlay, enqueueSnackbar, versionId, multiProjectId]);

  const gridOptions: GridOptions = {
    animateRows: true,
    defaultColDef: {
      sortable: false,
      suppressHeaderMenuButton: true,
    },
    rowGroupPanelShow: "never",
    sideBar: false,
    pagination: false,
    suppressMovableColumns: true,
    domLayout: "autoHeight",
    getRowId: (params: GetRowIdParams) => params.data.ID,
  };

  const projectColumns: ColDef[] = useMemo(
    () => [
      {
        field: "Name",
        headerName: "Overlay Name",
        flex: 2,
      },
      {
        field: "SourceVersion.Project.Name",
        headerName: "Source Project",
        flex: 2,
        cellRenderer: LinkCellRenderer,
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            href: `/project/${params.data?.SourceVersion?.Project?.ID}/`,
            target: "_blank",
          };
        },
      },
      {
        field: "SourceVersion.Name",
        headerName: "Source Version",
        flex: 2,
      },
      {
        colId: "edit",
        headerName: "",
        cellRenderer: IconButtonCellRenderer,
        cellRendererParams: (params: ICellRendererParams) => ({
          icon: Edit,
          iconButtonProps: {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              setEditingOverlay(params.data);
            },
            sx: { padding: 0.5 },
          },
        }),
        type: "rightAligned",
        width: 60,
        sortable: false,
        resizable: false,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
      },
      {
        colId: "delete",
        headerName: "",
        cellRenderer: IconButtonCellRenderer,
        cellRendererParams: (params: ICellRendererParams) => ({
          icon: Delete,
          iconButtonProps: {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              setRemovingProjectOverlay(params.data);
            },
            color: "error" as const,
            sx: { padding: 0.5 },
          },
        }),
        type: "rightAligned",
        width: 60,
        sortable: false,
        resizable: false,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
      },
    ],
    []
  );

  const imageColumns: ColDef[] = useMemo(
    () => [
      {
        field: "Name",
        headerName: "Image Name",
        flex: 2,
      },
      {
        colId: "delete",
        headerName: "",
        cellRenderer: IconButtonCellRenderer,
        cellRendererParams: (params: ICellRendererParams<ImageOverlayItem>) => ({
          icon: Delete,
          iconButtonProps: {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              if (params.data) {
                setRemovingImageOverlay(params.data);
              }
            },
            color: "error" as const,
            sx: { padding: 0.5 },
          },
        }),
        type: "rightAligned",
        width: 60,
        sortable: false,
        resizable: false,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
      },
    ],
    []
  );

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        header="Manage Overlays"
        variant="primary"
        showClose
        content={
          <Box style={{ minHeight: "300px" }}>
            <Tabs value={activeTab} onChange={(_e, val) => setActiveTab(val)} sx={{ mb: 2 }}>
              <Tab label="Project Overlays" />
              <Tab label="Image Overlays" />
            </Tabs>
            {activeTab === 0 && (
              <>
                <Box sx={{ mb: 3, color: "text.secondary" }}>
                  Overlays allow you to view layers from other projects on top of your current project. Add overlays to compare data or reference
                  information from different sources.
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                  {maxOverlays ? <Alert severity="warning">Maximum of {formatNumber(MAX_OVERLAY_COUNT)} overlays reached.</Alert> : <Box />}
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => setEditingOverlay({} as CustomOverlayItem)}
                    disabled={maxOverlays}
                  >
                    Add Overlay
                  </Button>
                </Box>
                <AgGrid columnDefs={projectColumns} rowData={overlayItems} gridOptions={gridOptions} variant="outlined" />
              </>
            )}
            {activeTab === 1 && (
              <>
                <Box sx={{ mb: 3, color: "text.secondary" }}>
                  Image overlays are raster images that have been aligned to map coordinates using ground control points.
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                  <Button variant="contained" color="primary" startIcon={<Add />} onClick={onOpenImageOverlay}>
                    Add Overlay
                  </Button>
                </Box>
                <AgGrid columnDefs={imageColumns} rowData={imageOverlayItems} gridOptions={gridOptions} variant="outlined" />
              </>
            )}
          </Box>
        }
        actions={
          <Button color="primary" onClick={onClose}>
            Close
          </Button>
        }
      />
      {editingOverlay && <EditOverlayModal overlay={editingOverlay} onClose={() => setEditingOverlay(null)} />}
      {removingProjectOverlay && (
        <ConfirmModal
          open
          header={`Remove "${removingProjectOverlay.Name}"`}
          confirmText="Remove"
          color="error"
          content={<Typography variant="body2">Are you sure you want to remove this overlay?</Typography>}
          onConfirm={handleRemoveProjectOverlay}
          onCancel={() => setRemovingProjectOverlay(null)}
        />
      )}
      {removingImageOverlay && (
        <ConfirmModal
          open
          header={`Remove "${removingImageOverlay.Name}"`}
          confirmText="Remove"
          color="error"
          content={<Typography variant="body2">Are you sure you want to remove this image overlay?</Typography>}
          onConfirm={handleRemoveImageOverlay}
          onCancel={() => setRemovingImageOverlay(null)}
        />
      )}
    </>
  );
};

export default OverlaysSettingModal;
